<template>
  <Panel
    class="item-codes-panel"
    background="#f2f2f2"
  >
    <template #content>
      <div
        v-if="groupedCodes"
        class="content"
      >
        <div
          v-for="group in groupedCodes"
          :key="group.category.id"
          class="group"
        >
          <strong
            class="group__title"
            :title="group.category.path"
          >
            {{ group.category.name }}
          </strong>
          <button
            v-for="code in group.codes"
            :key="code.id"
            :title="formatCodePath(code.path)"
            @click="() => copy(code.path)"
          >
            <PsTag
              size="md"
              color="light"
            >
              {{ code.name }}
            </PsTag>
          </button>
        </div>
      </div>
    </template>
  </Panel>
</template>

<script setup lang="ts">
import { useClipboard } from '@vueuse/core'
import { computed } from 'vue'
import Panel from '@/ContextTab/components/Panel/Panel.vue'
import PsTag from '@/ContextTab/components/UI/PsTag.vue'
import { shownCodeCatsOrder, formatCodePath } from '@/lib/codes'
import type { CodeWithCategoryAndChildren } from '@/api/generated/v2'
import type { NewsItemPublished } from '@/ContextTab/stores/types'


type GroupedCodes = {
  codes: Pick<CodeWithCategoryAndChildren, 'id' | 'name' | 'path'>[]
  category: Pick<CodeWithCategoryAndChildren, 'id' | 'name' | 'path'> & { order: number }
}

const props = defineProps<{
  item?: NewsItemPublished
}>()

const { copy } = useClipboard()

const groupedCodes = computed<GroupedCodes[]>(() => {
  const map = new Map<number, GroupedCodes>()

  // group codes by category
  props.item?.version?.codes?.forEach(({ code }) => {
    if (code.category) {
      const cat: GroupedCodes = map.get(code.category.id) ?? {
        codes: [],
        category: {
          id: code.category.id,
          name: code.category.name,
          path: code.category.path,
          order: shownCodeCatsOrder[code.category.path] ?? 0,
        },
      }

      cat.codes.push({
        id: code.id,
        name: code.name,
        path: code.path,
      })

      map.set(code.category.id, cat)
    }
  })

  // sort codes by name inside cat
  map.forEach((item) => item.codes.sort((a, b) => a.name.localeCompare(b.name)))

  // sort cats by predefined order
  return [...map]
    .sort((a, b) => a[1].category.order - b[1].category.order)
    .map((item) => item[1])
})
</script>

<style scoped>
.item-codes-panel {
  width: 100%;
}
.content {
  overflow-y: auto;
  padding: 1.2em 0.65em;

  display: flex;
  flex-direction: column;
  gap: 1em;

  color: #444;

  & .group {
    display: flex;
    flex-direction: column;
    gap: 0.3em;
    align-items: flex-start;
  }

  & .group__title {
    color: hsl(0 0% 50%);
    font-size: 0.85em;
    margin-bottom: 0.2em;
  }
}
</style>
